"use client";

import { ServiceCard } from "../ServiceCard";
import type { ServiceId } from "../GetStartedFlow";

// The newline in each title is deliberate — the design breaks card titles onto
// two lines regardless of available width (rendered via whitespace-pre-line).
const SERVICES: {
  id: ServiceId;
  title: string;
  description: string;
  image: string;
}[] = [
  {
    id: "register-business",
    title: "Register my\nbusiness",
    description:
      "Register your company, open business accounts, and get compliance support from day one.",
    image: "/get-started/step1.svg",
  },
  {
    id: "manage-existing-business",
    title: "Manage\nExisting Business",
    description: "Get help with accounting, compliance, filings, and company changes.",
    image: "/get-started/step1.svg",
  },
];

export function ChooseServiceStep({ onSelect }: { onSelect: (id: ServiceId) => void }) {
  return (
    <div className="mx-auto flex w-full max-w-[1240px] flex-col items-center px-4 pb-24 pt-14 sm:px-6 lg:pt-[44px]">
      <h1 className="max-w-[595px] text-center font-suisse-intl-bold text-4xl leading-tight text-white sm:text-5xl lg:text-[64px] lg:leading-[71px]">
        What would you like to do?
      </h1>
      <p className="mt-6 max-w-[695px] text-center font-suisse-intl-bold text-lg leading-[28.8px] text-white lg:mt-12">
        Choose the option that best matches your current business needs.
      </p>

      {/* max-w rather than a fixed w: the page no longer clips horizontal
          overflow (that clip was breaking sticky), so a hard 1100px would push
          a horizontal scrollbar onto anything narrower. */}
      {/* Two-up from lg rather than md: at 768-1023px two cards leave ~294px of
          content each, which the 32px titles barely clear. lg and up is
          unchanged — still two columns. */}
      <div className="mt-10 grid w-full max-w-[1100px] grid-cols-1 justify-items-center gap-6 sm:mt-16 sm:gap-8 lg:grid-cols-2">
        {SERVICES.map((service) => (
          <button
            key={service.id}
            type="button"
            onClick={() => onSelect(service.id)}
            className="group relative flex w-full max-w-[590px] pt-6 focus-visible:outline-none lg:pt-[42px]"
          >
            {/* Ambient ellipse behind the card face. The design specifies this
                as a 50%-white ellipse filling the frame with a 533px *backdrop*
                blur, but backdrop-filter blurs what sits behind an element, not
                its own fill — over a smooth gradient that is a no-op, leaving a
                hard-edged white ellipse. A filter blur is what actually renders
                Figma's soft glow, so that's what's used here. */}
            <div
              aria-hidden
              className="pointer-events-none absolute top-24 inset-0 rounded-full bg-white/50 blur-3xl"
            />
            <ServiceCard
              title={service.title}
              description={service.description}
              image={service.image}
              className="relative transition-transform duration-200 group-hover:-translate-y-1 group-focus-visible:outline-[#0061FE]"
            />
          </button>
        ))}
      </div>
    </div>
  );
}
